/** Floor-authored red bulkhead fitting; the room owns its actual illumination. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';
import { getZone, type ZonePalette } from '@/render/palette';

const zone: ZonePalette = (() => {
  const found = getZone('cull');
  if (!found) throw new Error('bulkhead lamp: cull zone missing from the palette');
  return found;
})();

function zoneSurface(index: number): number {
  const colour = zone.surfaces[index];
  if (colour === undefined) throw new Error(`bulkhead lamp: cull surface ${String(index)} missing`);
  return colour;
}

function metalBox(size: readonly [number, number, number], material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(...size), material);
  mesh.name = name;
  mesh.castShadow = true;
  return mesh;
}

/**
 * Half the mounting depth, subtracted from every part's Z.
 *
 * **This asset used to be authored from its back plate forward**, spanning
 * z 0…0.14, which put the centre of its bounds at 0.07 and failed
 * `asset.spec.ts`'s "centred in X/Z" check by more than double its tolerance.
 * The contract is not decoration: `wall-vent` and `personnel-door` are both
 * wall pieces and both are authored centred on their own depth, so a caller can
 * place any of the three the same way. This one could not be.
 *
 * The mounting face is now `+DEPTH_HALF` in local Z, and `cullCorridor.ts` backs
 * the placement off by exactly this much so the lamp still sits where it sat.
 */
const DEPTH_HALF = 0.07;

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'bulkhead-lamp';
  const cageMaterial = surface('metal', zoneSurface(0));
  const accent = zone.accent;
  if (accent === undefined) throw new Error('bulkhead lamp: cull accent missing from the palette');
  const lensMaterial = surface('mineral', accent);

  const plate = metalBox([0.28, 0.30, 0.02], cageMaterial, 'wall-back-plate');
  plate.position.set(0, 0.15, 0.01 - DEPTH_HALF);
  // The flattened sphere creates a truly domed front while preserving the
  // 0.14 m mounting depth. A cylinder here read as a red button at corridor
  // distance, which fights the one directional light the beat is allowed.
  const lens = new THREE.Mesh(new THREE.SphereGeometry(0.115, 12, 8), lensMaterial);
  lens.name = 'red-domed-lens';
  lens.scale.z = 0.5;
  lens.position.set(0, 0.15, 0.0775 - DEPTH_HALF);
  lens.castShadow = true;
  group.add(plate, lens);

  for (const [index, x] of [-0.075, 0, 0.075].entries()) {
    const bar = metalBox([0.012, 0.22, 0.012], cageMaterial, `cage-vertical-${index + 1}`);
    bar.position.set(x, 0.15, 0.134 - DEPTH_HALF);
    group.add(bar);
  }
  const crossbar = metalBox([0.23, 0.012, 0.012], cageMaterial, 'cage-crossbar');
  crossbar.position.set(0, 0.15, 0.134 - DEPTH_HALF);
  group.add(crossbar);

  return group;
}

export const bulkheadLampAsset: AssetDefinition = {
  id: 'bulkhead-lamp',
  label: 'Red caged bulkhead',
  description: 'Floor-authored wall bulkhead with the cull corridor’s sole red lens.',
  footprint: { width: 0.28, height: 0.30, length: 0.14 },
  create: build,
};
